import React from 'react';
import { Helmet } from 'react-helmet-async';
import { HeartIcon, SparklesIcon, ShieldCheckIcon, UsersIcon } from '@heroicons/react/24/outline';
import NewsletterSignup from '../components/NewsletterSignup';
import { useScrollToTop } from '../hooks/useScrollToTop';

const AboutPage: React.FC = () => {
  // Scroll to top when page loads
  useScrollToTop();
  return (
    <>
      <Helmet>
        <title>About Destress Daily - Your Trusted Source for Daily Destress Tips</title>
        <meta name="description" content="Learn about Destress Daily - your trusted source for practical destress tips, stress relief strategies, and evidence-based wellness advice to help you destress effectively." />
        <meta name="keywords" content="about destress daily, destress tips website, stress relief blog, wellness advice, how to destress, destress techniques" />
        <meta property="og:title" content="About Destress Daily - Your Trusted Source for Daily Destress Tips" />
        <meta property="og:description" content="Learn about Destress Daily - your trusted source for practical destress tips and stress relief strategies." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://destress-daily.com/about" />
        <link rel="canonical" href="https://destress-daily.com/about" />
      </Helmet>

      <div className="pt-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-health-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-gradient rounded-2xl mb-6">
                <HeartIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
                About Destress Daily
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Your trusted source for practical stress relief tips, mindfulness advice, and evidence-based wellness information to help you live a calmer, more balanced life.
              </p>
            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Mission
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We believe that stress relief shouldn't be complicated. Our mission is to provide simple, practical, and evidence-based stress relief techniques that anyone can implement in their daily life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card">
              <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center mb-4">
                <HeartIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Wellness-First Approach</h3>
              <p className="text-gray-600">
                Every piece of content we publish is carefully researched and reviewed to ensure it promotes genuine stress relief and follows evidence-based wellness practices.
              </p>
            </div>

            <div className="card">
              <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center mb-4">
                <SparklesIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Practical Solutions</h3>
              <p className="text-gray-600">
                We focus on actionable stress relief techniques and mindfulness practices that you can implement immediately, without requiring expensive equipment or drastic lifestyle changes.
              </p>
            </div>

            <div className="card">
              <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center mb-4">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Trusted Information</h3>
              <p className="text-gray-600">
                Our content is written by wellness enthusiasts and reviewed by stress management experts to ensure accuracy and safety.
              </p>
            </div>

            <div className="card">
              <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center mb-4">
                <UsersIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Community Focused</h3>
              <p className="text-gray-600">
                We're building a community of stress-conscious individuals who support and inspire each other on their wellness and mindfulness journeys.
              </p>
            </div>
          </div>
        </div>

        {/* Story Section */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Destress Daily was born from a simple observation: there's too much conflicting wellness information out there, and most of it is either too complex or too vague to be useful for stress relief.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Our founder, a wellness enthusiast with years of experience researching stress relief practices, noticed that people were constantly asking for simple, practical stress relief tips that they could implement immediately. They wanted actionable advice, not overwhelming information.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                That's why we created Destress Daily - a platform that delivers clear, concise, and practical stress relief tips every day. We focus on evidence-based information that's easy to understand and implement.
              </p>
              
              <p className="text-lg text-gray-600">
                Today, we serve thousands of readers worldwide, helping them make small but meaningful improvements to their stress levels and mental wellness every day.
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Values
            </h2>
          </div>

          <div className="space-y-8">
            <div className="flex items-start">
              <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Evidence-Based</h3>
                <p className="text-gray-600">
                  Every tip and piece of advice we share is backed by scientific research and reviewed by stress management experts.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Practical & Actionable</h3>
                <p className="text-gray-600">
                  We focus on stress relief techniques that you can implement immediately, without requiring expensive equipment or drastic lifestyle changes.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Accessible to Everyone</h3>
                <p className="text-gray-600">
                  Stress relief information should be available to everyone, regardless of their background, budget, or current stress levels.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Continuous Learning</h3>
                <p className="text-gray-600">
                  We're constantly learning and updating our content to reflect the latest stress relief research and best practices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Signup */}
      <NewsletterSignup variant="hero" />
    </>
  );
};

export default AboutPage; 